<template>
  <div class="reference-lookup-input" :class="{ inactive: query.length < minLen, 'no-results': visibleItems.length === 0 }">
    <b-form-input
      ref="input"
      :value="localText"
      :placeholder="placeholder"
      autocomplete="off"
      class="bg-dark text-white border-secondary reference-lookup-control"
      @input="onInput"
      @focus="focused = true"
      @blur="onBlur"
      @keydown.down.prevent="moveActive(1)"
      @keydown.up.prevent="moveActive(-1)"
      @keydown.enter.prevent="selectActive"
      @keydown.esc.prevent="close"
    ></b-form-input>

    <div v-if="showList" class="reference-lookup-list">
      <button
        v-for="(option, index) in visibleItems"
        :key="option.id + '-' + option.label"
        type="button"
        class="reference-lookup-option"
        :class="{ active: index === activeIndex }"
        @mousedown.prevent="selectOption(option)"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReferenceLookupInput',
  props: {
    maxResults: {
      type: Number,
      default: 10
    },
    minLen: {
      type: Number,
      default: 1
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      activeIndex: 0,
      dirtySent: false,
      focused: false,
      localText: ''
    }
  },
  computed: {
    query () {
      return String(this.localText || '').trim().toLowerCase()
    },
    showList () {
      return this.focused && this.query.length >= this.minLen && this.visibleItems.length > 0
    },
    visibleItems () {
      if (this.query.length < this.minLen) return []
      const result = []
      const options = this.options || []
      for (let index = 0; index < options.length; index += 1) {
        const option = options[index]
        if (String(option.search || option.label || '').toLowerCase().indexOf(this.query) === -1) continue
        result.push(option)
        if (result.length >= this.maxResults) break
      }
      return result
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (value) {
        this.localText = String(value || '')
        this.dirtySent = false
        this.activeIndex = 0
      }
    },
    visibleItems () {
      if (this.activeIndex >= this.visibleItems.length) {
        this.activeIndex = Math.max(this.visibleItems.length - 1, 0)
      }
    }
  },
  methods: {
    close () {
      this.focused = false
    },
    moveActive (direction) {
      const count = this.visibleItems.length
      if (!count) return
      this.activeIndex = (this.activeIndex + direction + count) % count
    },
    onBlur () {
      window.setTimeout(() => {
        this.focused = false
      }, 120)
    },
    onInput (value) {
      this.localText = String(value || '')
      this.focused = true
      this.activeIndex = 0
      if (this.value && !this.dirtySent) {
        this.dirtySent = true
        this.$emit('dirty')
      }
    },
    selectActive () {
      if (!this.visibleItems.length) return
      this.selectOption(this.visibleItems[this.activeIndex] || this.visibleItems[0])
    },
    selectOption (option) {
      if (!option) return
      this.localText = option.label
      this.focused = false
      this.dirtySent = false
      this.$emit('selected', option)
    }
  }
}
</script>

<style scoped>
.reference-lookup-input {
  position: relative;
}

.reference-lookup-control {
  min-height: 35px;
}

.reference-lookup-list {
  background: #2f353a;
  border: 1px solid #3e515b;
  border-radius: 4px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.28);
  left: 0;
  margin-top: 4px;
  max-height: 260px;
  overflow-x: hidden;
  overflow-y: auto;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 1060;
}

.reference-lookup-option {
  background: #2f353a;
  border: 0;
  border-bottom: 1px solid #3e515b;
  color: #d7dee6;
  cursor: pointer;
  display: block;
  overflow-wrap: anywhere;
  padding: 7px 10px;
  text-align: left;
  white-space: normal;
  width: 100%;
}

.reference-lookup-option:hover,
.reference-lookup-option.active {
  background: #26313b;
  color: #fff;
}
</style>
